import { useState } from 'react';

import clsx from 'clsx';

// Hooks
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Libs
import { deleteApi } from '@/utils/client';

import PropTypes from 'prop-types';

import { networksNotAcceptedByScheduler } from '@/lib/publishing/networksNotAcceptedByScheduler';

import { PROFILE_TYPE_GROUP } from '@/components/base/Avatar';
// Components
import { DropDownFilter } from '@/components/base/DropdownFilter/DropdownFilter';
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import SelectWithSPIcon from '@/components/base/Select/SelectWithSPIcon';
import SelectFilter from '@/components/base/SelectFilter';
import Tooltip from '@/components/base/Tooltip';
import SpOrSpGroupAvatar from '@/components/composite/SpGroups/SpOrSpGroupAvatar';

// Routes
import { COLLABORATION_NOTIFICATIONS } from '@/routes/apiEndpoints';

const ButtonClass =
    'w-12 h-12 rounded-full bg-white text-icoGray-500 transition-all hover:shadow-icoM dark:bg-icoDarkMode-anthracite dark:text-white flex items-center justify-center';

const SpOption = ({ option, selected, active }) => {
    return (
        <div className="flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf">
            {option?.value === 'all' ? (
                <Icon
                    className={clsx('mr-px px-2', { 'text-icoBlue': active || selected })}
                    icon="single-profile"
                />
            ) : (
                <SpOrSpGroupAvatar
                    item={option}
                    size="sm"
                    profileNetworkType={true}
                    profileGroupType={true}
                    disabled={option?.disabled}
                />
            )}

            <div className="flex flex-1 flex-col overflow-hidden pl-3.5 text-left">
                <span
                    className={clsx('truncate font-GSemiBold text-sm transition-all', {
                        'text-icoBlue': selected || active,
                        'text-icoBlack dark:text-white': !selected && !active && !option.disabled,
                        'text-icoGray-400 dark:text-icoDarkMode-wolf': option.disabled
                    })}>
                    {option?.slug ?? option?.label}
                </span>
                {option?.value !== 'all' ? (
                    <span
                        className={clsx('truncate font-GMedium text-xs transition-all', {
                            'text-icoBlue': selected || active,
                            'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                !active && !selected && !option.disabled,
                            'text-icoGray-400 dark:text-icoDarkMode-wolf': option.disabled
                        })}>
                        {option?.label}
                    </span>
                ) : null}
            </div>
        </div>
    );
};

SpOption.propTypes = {
    option: PropTypes.object,
    selected: PropTypes.bool,
    active: PropTypes.bool
};

const getUnreadCount = (notifications, unreadCount, deletedIds) => {
    if (typeof unreadCount === 'string') {
        return unreadCount;
    }

    const unreadNotification = notifications?.filter(
        (notification) => deletedIds?.includes(notification?.id) && notification?.is_read === false
    );

    if (!unreadNotification?.length) return unreadCount;

    if (unreadNotification?.length && unreadCount - unreadNotification?.length >= 0) {
        return unreadCount - unreadNotification?.length;
    }

    return 0;
};

const notificationFilters = [
    {
        name: 'scheduler:collaboration.notifications.all_notifications',
        status: null
    },
    {
        name: 'scheduler:collaboration.notifications.new_notifications',
        status: 'unread'
    },
    {
        name: 'scheduler:collaboration.notifications.read_notifications',
        status: 'read'
    }
];

const CollaborationNotificationsHeader = ({
    setRequestParams,
    mutateNotifications,
    notifications,
    checkedIds,
    setCheckedIds,
    isValidating
}) => {
    const { t } = useTranslation('scheduler');
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { activatedSpGroups } = useSPGroups({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const [selectedProfile, setSelectedProfile] = useState('all');
    const [deleteStatus, setDeleteStatus] = useState({
        isLoading: false,
        error: null
    });
    const [notificationFilter, setNotificationFilter] = useState(notificationFilters[0]);

    const selectOptions = [
        {
            label: t(
                activatedSpGroups?.length
                    ? 'collaboration.notifications.all_social_profiles_and_groups'
                    : 'collaboration.notifications.all_social_profiles'
            ),
            value: 'all'
        },
        ...activatedSpGroups.map((profile) => ({
            label: profile?.name,
            value: `group-${profile?.id}`,
            group_id: profile?.id,
            ...profile
        })),
        ...activatedSocialProfiles.map((profile) => ({
            label: profile?.slug,
            value: profile?.id,
            disabled: networksNotAcceptedByScheduler?.includes(profile?.social_network_type),
            ...profile
        }))
    ];

    const deleteIds = async () => {
        setDeleteStatus({
            error: null,
            isLoading: true
        });

        try {
            await deleteApi(COLLABORATION_NOTIFICATIONS, {
                data: {
                    ids: checkedIds
                }
            });
            mutateNotifications(
                (prevState) =>
                    prevState?.map((page) => ({
                        ...page,
                        data: page?.data?.filter(
                            (notificationItem) => !checkedIds?.includes(notificationItem?.id)
                        ),
                        summary: {
                            ...page?.summary,
                            unread_count: getUnreadCount(
                                notifications?.flatMap((page) => page?.data),
                                page?.summary?.unread_count,
                                checkedIds
                            )
                        }
                    })),
                false
            );
            setCheckedIds([]);
            setDeleteStatus((prevState) => ({
                ...prevState,
                isLoading: false
            }));
        } catch (e) {
            setDeleteStatus({
                error: t('errors:error_occured_submit_data'),
                isLoading: false
            });
        }
    };

    return (
        <div className="manipulation-header mb-10 flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            <DropDownFilter
                size="full"
                classNameWrapper="manipulation-header z-1"
                currentFilter={notificationFilter}
                filters={notificationFilters}
                setFilter={(filter) => {
                    setNotificationFilter(filter);
                    setRequestParams((prevState) => ({
                        ...prevState,
                        status: filter?.status ?? undefined
                    }));
                }}
                buttonClassName="font-GBold text-xl"
                className="text-base"
            />

            <div className="flex flex-1 items-center justify-end space-x-3">
                <div className="w-full flex-1 lg:w-64 lg:flex-initial">
                    <SelectFilter
                        selected={
                            selectOptions?.filter(
                                (option) => option?.value === selectedProfile
                            )?.[0]
                        }
                        setSelected={(p) => {
                            setSelectedProfile(p?.value ?? 'all');

                            let requestParams;
                            if (p?.group_id) {
                                requestParams = {
                                    social_profile_id: undefined,
                                    group_id: p?.id
                                };
                            } else {
                                requestParams = {
                                    group_id: undefined,
                                    social_profile_id: p?.id
                                };
                            }

                            setRequestParams((prevState) => ({
                                ...prevState,
                                ...requestParams
                            }));
                        }}
                        idInput="select-filter-collaboration-settings"
                        options={selectOptions}
                        button={
                            <SelectWithSPIcon
                                id="select-filter-collaboration-settings"
                                baseStyle="noBorder"
                                profileData={
                                    selectOptions?.filter(
                                        (option) => option?.value === selectedProfile
                                    )?.[0]
                                }
                                socialNetworkType={
                                    selectOptions?.filter(
                                        (option) => option?.value === selectedProfile
                                    )?.[0]?.social_network_type ?? PROFILE_TYPE_GROUP
                                }
                            />
                        }>
                        <SpOption />
                    </SelectFilter>
                </div>

                <Tooltip content={t('collaboration.notifications.refresh_list')} placement="top">
                    <button
                        onClick={() => mutateNotifications()}
                        disabled={isValidating}
                        className={ButtonClass}
                        data-testid="refresh-notifications-button">
                        {isValidating ? (
                            <Loader className="h-4 w-4 text-icoBlue" />
                        ) : (
                            <Icon icon="refresh" />
                        )}
                    </button>
                </Tooltip>

                <Tooltip
                    content={t('collaboration.notifications.delete_notifications')}
                    placement="top">
                    <button
                        onClick={deleteIds}
                        className={ButtonClass}
                        disabled={deleteStatus?.isLoading || !checkedIds.length}
                        data-testid="delete-notifications-button">
                        {deleteStatus?.isLoading ? (
                            <Loader className="h-4 w-4 text-icoBlue" />
                        ) : (
                            <Icon icon="trash" />
                        )}
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

CollaborationNotificationsHeader.propTypes = {
    setRequestParams: PropTypes.func,
    mutateNotifications: PropTypes.func,
    notifications: PropTypes.array,
    checkedIds: PropTypes.array,
    setCheckedIds: PropTypes.func,
    isValidating: PropTypes.bool
};
export default CollaborationNotificationsHeader;
